import { ButtonTabs } from '@edu-frontend/modules/src/shared/ui/ButtonTabs';
import React, { CSSProperties, useCallback, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { IconBook, IconPlay, IconStop } from '@edu-frontend/modules/src/components/ui/Icon';
import { LESSON_HEADER_HEIGHT } from '@edu-frontend/modules/src/features/lessonHeaderMobile';
import { Code } from '@edu-frontend/modules/src/shared/ui/Code';
import cx from 'classnames';
import { RadioButton } from '@edu-frontend/modules/src/components/ui/RadioButton';
import { MOBILE_TABS_HEIGHT } from '../../../consts';
import { RobotFieldPane } from '../../../__field-pane/robot__field-pane';
import RobotInfo from '../../../__info/robot__info';
import { Blockly } from '../../../__code-container/robot__code-container';
import { RobotState, useRobotState } from '../../../robotState';
import { useRobotSelector } from '../../../context';
import RobotError from '../../../__error/robot__error';

// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import styles from './robot_view_blockly-python_mobile.module.scss';

const TABS = [
    {
        id: 'task',
        title: 'robotProblem.blocklyPythonMobile.tabs.task',
        Icon: IconBook,
    },
    {
        id: 'code',
        title: 'robotProblem.blocklyPythonMobile.tabs.blockly',
        Icon: IconStop,
    },
    {
        id: 'robot',
        title: 'robotProblem.blocklyPythonMobile.tabs.robot',
        Icon: IconPlay,
    },
] as const;

const RADIO_BUTTON_OPTIONS = [
    { value: 'blocks', children: 'Блоки' },
    { value: 'code', children: 'Код' },
];

export const RobotViewBlocklyPythonMobile = () => {
    const intl = useIntl();
    const { setMobileTab, mobileTab: activeTab } = useRobotState();
    const currentCode = useRobotSelector((s) => s.codeString);
    const [radioValue, setRadioValue] = useState('blocks');

    const renderTabContent = useCallback(
        (tabId: RobotState['mobileTab']) => {
            switch (tabId) {
                case 'task':
                    return <RobotInfo mix={styles.info} />;
                case 'code':
                    return radioValue === 'blocks' ? (
                        <Blockly />
                    ) : (
                        <div className={styles.code}>
                            <RobotError className={styles.error} />
                            <Code
                                editable={false}
                                options={{
                                    foldGutter: false,
                                    allowMultipleSelections: true,
                                    lineNumbers: true,
                                    autocompletion: false,
                                }}
                                styleSettings={{ fontSize: 'var(--hr-typography-font-size-m)' }}
                                value={currentCode}
                            />
                        </div>
                    );
                case 'robot':
                    return <RobotFieldPane className={styles.fieldPane} />;
                default:
                    return null;
            }
        },
        [radioValue]
    );

    useEffect(() => {
        // У lesson-run есть <img> который является голубым фоном урока
        const backgroundImage = document.getElementsByClassName('lesson-run__background-image');
        backgroundImage[0]?.remove();
    }, []);

    useEffect(() => {
        return () => {
            setMobileTab('task');
        };
    }, [setMobileTab]);

    return (
        <>
            <ButtonTabs
                className={styles.tabs}
                style={{ ['--lesson-mobile-header-height']: LESSON_HEADER_HEIGHT } as CSSProperties}
                tabListLabel={intl.formatMessage({ id: 'a11y.robotProblem.blocklyPythonMobile.tabs' })}
                tabMaxWidth
                tabs={TABS.map(({ id, title, Icon }) => ({
                    id,
                    title: intl.formatMessage({ id: title }),
                    icon: <Icon size="m" />,
                    isActive: activeTab === id,
                    onClick: () => setMobileTab(id),
                }))}
            />
            <div
                className={cx(styles.contentWrapper, activeTab === 'robot' && styles.contentWrapper_robot)}
                style={
                    {
                        ['--lesson-mobile-tabs-height']: MOBILE_TABS_HEIGHT,
                    } as CSSProperties
                }
            >
                <div className={styles.topButtonPanel}>
                    {activeTab === 'code' && (
                        <div className={styles.radioButton}>
                            <RadioButton
                                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                                    const currentValue = event.target.value;

                                    setRadioValue(currentValue);
                                }}
                                options={RADIO_BUTTON_OPTIONS}
                                size="m"
                                value={radioValue}
                                view="default"
                            />
                        </div>
                    )}
                </div>
                {renderTabContent(activeTab)}
            </div>
        </>
    );
};
